import { Button, ManageableItemsList } from '@payaca/ambrosia';
import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { readableProjectIntegrationService } from '@payaca/helpers/projectIntegrationsHelper';
import { ProjectIntegrationService } from '@payaca/types/projectIntegrationTypes';
import widen from '@payaca/utilities/widen';
import { formatDate, isWithinInterval, subHours } from 'date-fns';
import { FC } from 'react';
import useGetMyAccountIntegrations from '../../../../api/queries/me/useGetMyAccountIntegrations';
import useGetMyRegionPreferences from '../../../../api/queries/me/useGetMyRegionPreferences';
import useGetProjectIntegrations from '../../../../api/queries/project/useGetProjectIntegrations';
import { useModal } from '../../../../hooks/useModal';
import CreateLinkedProjectModal from './ProjectIntegrationsPanel/CreateLinkedProjectModal';
import PushToSpruceModal from './ProjectIntegrationsPanel/PushToSpruceModal';
import SyncFilesConfirmationModal from './ProjectIntegrationsPanel/SyncFilesConfirmationModal';
import SyncMaterialsConfirmationModal from './ProjectIntegrationsPanel/SyncMaterialsConfirmationModal';

const isWithinHour = (d: string) =>
  isWithinInterval(new Date(d), {
    start: subHours(new Date(), 1),
    end: new Date(),
  });
type Modals = {
  CREATE_LINKED_PROJECT: undefined;
  PUSH_TO_SPRUCE: undefined;
  SYNC_PROJECT_FILES: ProjectIntegrationItem;
  SYNC_PROJECT_MATERIALS: ProjectIntegrationItem;
};

const SUPPORTED_SERVICES = [
  'EASY_PV',
  'OPEN_SOLAR',
  'HEATPUNK',
  'SPRUCE',
] satisfies ProjectIntegrationService[];

function isSupportedService(
  service: string
): service is (typeof SUPPORTED_SERVICES)[number] {
  return widen<string[]>(SUPPORTED_SERVICES).includes(service);
}
const canServiceSyncFiles = (service: ProjectIntegrationService): boolean => {
  return ['EASY_PV', 'HEATPUNK', 'OPEN_SOLAR'].includes(service);
};
const canServiceSyncMaterials = (
  service: ProjectIntegrationService
): boolean => {
  return ['EASY_PV', 'HEATPUNK', 'OPEN_SOLAR'].includes(service);
};

type ProjectIntegrationItem = {
  id: string;
  externalUrl?: string;
  service: ProjectIntegrationService;
  filesLastSyncedAt?: string;
  filesSyncStartedAt?: string;
  filesSynced?: number;
  materialsLastSyncedAt?: string;
  materialsSyncStartedAt?: string;
  materialsSynced?: number;
};
type Props = {
  projectId: string;
};

export const ProjectIntegrationsPanel: FC<Props> = (props) => {
  const { projectId } = props;
  const { data: accountRegionPreferences } = useGetMyRegionPreferences();
  const { data: myAccountIntegrations } = useGetMyAccountIntegrations();
  const { integrations: projectIntegrations, isLoading } =
    useGetProjectIntegrations(projectId);

  const canCreateLinkedProject =
    myAccountIntegrations.length > 0 &&
    // check not all account integrations are already linked to the project
    myAccountIntegrations.length !==
      myAccountIntegrations.filter((integration) =>
        projectIntegrations?.find((p) => p.service === integration.service)
      ).length;
  const [modalState, modalDispatch] = useModal<keyof Modals, Modals>({
    activeModal: null,
  });

  const projectIntegrationsItems: ProjectIntegrationItem[] = (
    projectIntegrations || []
  ).flatMap((integration) =>
    isSupportedService(integration.service)
      ? [
          {
            id: integration.publicId,
            externalUrl: integration.externalUrl ?? undefined,
            service: integration.service,
            filesLastSyncedAt: integration.filesLastSyncedAt ?? undefined,
            filesSyncStartedAt: integration.filesSyncStartedAt ?? undefined,
            filesSynced: integration.filesSynced ?? undefined,
            materialsLastSyncedAt:
              integration.materialsLastSyncedAt ?? undefined,
            materialsSyncStartedAt:
              integration.materialsSyncStartedAt ?? undefined,
            materialsSynced: integration.materialsSynced ?? undefined,
          },
        ]
      : []
  );

  const getItemActions = (row: ProjectIntegrationItem) => {
    if (!canServiceSyncFiles(row.service)) {
      return [];
    }

    const accountHasIntegrationEnabled = myAccountIntegrations.some(
      (accountIntegration) => accountIntegration.service === row.service
    );

    const serviceName = readableProjectIntegrationService[row.service];
    const canManualSyncFiles = ['EASY_PV', 'HEATPUNK'].includes(row.service);
    const canManualSyncMaterials = [
      'EASY_PV',
      'HEATPUNK',
      'OPEN_SOLAR',
    ].includes(row.service);
    if (!canManualSyncFiles && !canManualSyncMaterials) {
      return [
        {
          label: `${serviceName} syncs automatically`,
          isDisabled: () => true,
          onClick: () => {},
        },
      ];
    }
    const actions: {
      label: string;
      onClick: () => void;
      isDisabled: () => boolean;
    }[] = [];
    if (canManualSyncFiles) {
      actions.push({
        label: 'Sync Files',
        onClick: () => {
          modalDispatch({
            type: 'SYNC_PROJECT_FILES',
            payload: row,
          });
        },
        isDisabled: () =>
          !accountHasIntegrationEnabled ||
          (!!row.filesSyncStartedAt && isWithinHour(row.filesSyncStartedAt)),
      });
    }
    if (canManualSyncMaterials) {
      actions.push({
        label: 'Sync Materials',
        onClick: () => {
          modalDispatch({
            type: 'SYNC_PROJECT_MATERIALS',
            payload: row,
          });
        },
        isDisabled: () =>
          !accountHasIntegrationEnabled ||
          (!!row.materialsSyncStartedAt &&
            isWithinHour(row.materialsSyncStartedAt)),
      });
    }
    return actions;
  };

  return (
    <>
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="Integrations"
          subHeading="Sync Files from your linked Project integrations."
          buttons={
            canCreateLinkedProject ? (
              <Button
                size={'sm'}
                onClick={() => modalDispatch({ type: 'CREATE_LINKED_PROJECT' })}
                variant="white"
              >
                Create linked Project
              </Button>
            ) : undefined
          }
        />
        <ManageableItemsList.Table
          items={projectIntegrationsItems}
          uniqueKey={'id'}
          isLoading={isLoading}
          itemActions={getItemActions}
        >
          <ManageableItemsList.Table.Column<ProjectIntegrationItem, 'service'>
            header="Integration"
            field="service"
            render={(service, row) => {
              return row.externalUrl ? (
                <a
                  href={row.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {readableProjectIntegrationService[service]}
                </a>
              ) : (
                readableProjectIntegrationService[service]
              );
            }}
          />
          <ManageableItemsList.Table.Column
            header="Files Last Synced"
            field="filesLastSyncedAt"
            render={(filesLastSyncedAt, row) => {
              if (!canServiceSyncFiles(row.service)) {
                return 'N/A';
              }
              if (
                row.filesSyncStartedAt &&
                isWithinHour(row.filesSyncStartedAt)
              ) {
                return <span>Syncing...</span>;
              }
              if (filesLastSyncedAt) {
                return (
                  <span>
                    {formatDate(
                      filesLastSyncedAt,
                      getInternationalDateFormatByLocale(
                        DateFormats.TIMESTAMP_SHORT,
                        accountRegionPreferences?.locale
                      )
                    )}
                  </span>
                );
              }
            }}
          />
          <ManageableItemsList.Table.Column
            header="Files Synced"
            field="filesSynced"
            render={(filesSynced, row) =>
              !canServiceSyncFiles(row.service)
                ? 'N/A'
                : row.filesLastSyncedAt
                  ? filesSynced
                  : '—'
            }
          />
          <ManageableItemsList.Table.Column
            header="Materials Last Synced"
            field="materialsLastSyncedAt"
            render={(materialsLastSyncedAt, row) => {
              if (!canServiceSyncMaterials(row.service)) {
                return 'N/A';
              }
              if (
                row.materialsSyncStartedAt &&
                isWithinHour(row.materialsSyncStartedAt)
              ) {
                return <span>Syncing...</span>;
              }
              if (materialsLastSyncedAt) {
                return (
                  <span>
                    {formatDate(
                      materialsLastSyncedAt,
                      getInternationalDateFormatByLocale(
                        DateFormats.TIMESTAMP_SHORT,
                        accountRegionPreferences?.locale
                      )
                    )}
                  </span>
                );
              }
            }}
          />
          <ManageableItemsList.Table.Column
            header="Materials Synced"
            field="materialsSynced"
            render={(materialsSynced, row) =>
              !canServiceSyncMaterials(row.service)
                ? 'N/A'
                : row.materialsLastSyncedAt
                  ? materialsSynced
                  : '—'
            }
          />
        </ManageableItemsList.Table>
      </ManageableItemsList>
      <SyncFilesConfirmationModal
        isOpen={modalState.activeModal === 'SYNC_PROJECT_FILES'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
        service={modalState.payload?.service}
        projectId={projectId}
        projectIntegrationId={modalState.payload?.id}
      />
      <SyncMaterialsConfirmationModal
        isOpen={modalState.activeModal === 'SYNC_PROJECT_MATERIALS'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
        service={modalState.payload?.service}
        projectId={projectId}
        projectIntegrationId={modalState.payload?.id}
      />
      <PushToSpruceModal
        isOpen={modalState.activeModal === 'PUSH_TO_SPRUCE'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
        projectId={projectId}
      />
      <CreateLinkedProjectModal
        isOpen={modalState.activeModal === 'CREATE_LINKED_PROJECT'}
        onClose={() => modalDispatch({ type: 'CLOSE_MODAL' })}
        onSelectSpruce={() => modalDispatch({ type: 'PUSH_TO_SPRUCE' })}
        projectId={projectId}
      />
    </>
  );
};
